import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { 
  Box, 
  TextField, 
  Button, 
  Typography, 
  Container, 
  Paper,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  InputAdornment,
  IconButton,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { authService } from '../../services/authService';

const getValidationSchema = (phoneCountry) => yup.object({
  email: yup
    .string()
    .email('Введите корректный email')
    .required('Email обязателен'),
  fullName: yup
    .string()
    .required('Полное имя обязательно'),
  phone: yup
    .string()
    .required('Номер телефона обязателен')
    .test('phone-format', 'Введите корректный белорусский номер', function(value) {
      if (!value) return false;
      return /^\+375\d{9}$/.test(value);
      // Russian numbers temporarily disabled
      // if (phoneCountry === 'BY') {
      //   return /^\+375\d{9}$/.test(value);
      // } else {
      //   return /^\+7\d{10}$/.test(value);
      // }
    }),
  password: yup
    .string()
    .min(8, 'Пароль должен содержать минимум 8 символов')
    .required('Пароль обязателен'),
  companyName: yup
    .string()
    .when('role', {
      is: 'client',
      then: (schema) => schema.required('Название компании обязательно'),
      otherwise: (schema) => schema.notRequired()
    }),
  referralCode: yup
    .string(),
  role: yup
    .string()
    .oneOf(['respondent','client']).required()
});

// SMS verification is skipped for now

export default function Register() {
  const { role: roleParam } = useParams();
  const effectiveRole = 'respondent';
  const [registrationError, setRegistrationError] = useState('');
  const [verificationStep, setVerificationStep] = useState(false);
  const [verificationData, setVerificationData] = useState({ phone: '', userId: '' });
  const [verificationCode, setVerificationCode] = useState('');
  const [verificationError, setVerificationError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [phoneCountry, setPhoneCountry] = useState('BY'); // BY or RU
  const [isRegistering, setIsRegistering] = useState(false); // Loading state for registration
  const [showPassword, setShowPassword] = useState(false);
  
  const location = useLocation();
  const navigate = useNavigate();

  // Extract referral code from URL or localStorage if present
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const ref = params.get('ref');
    const storedRef = localStorage.getItem('pendingReferralCode');
    
    if (ref) {
      formik.setFieldValue('referralCode', ref);
      localStorage.removeItem('pendingReferralCode'); // Clear after use
    } else if (storedRef) {
      formik.setFieldValue('referralCode', storedRef);
      localStorage.removeItem('pendingReferralCode'); // Clear after use
    }
  }, [location]);

  // Timer for resend button
  useEffect(() => {
    if (resendTimer > 0) {
      const interval = setInterval(() => {
        setResendTimer(prev => prev - 1);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [resendTimer]);

  const formik = useFormik({
    initialValues: {
      email: '',
      fullName: '',
      phone: '',
      password: '',
      role: effectiveRole,
      referralCode: '',
      companyName: ''
    },
    validationSchema: getValidationSchema(phoneCountry),
    onSubmit: (values) => {
      handleRegistration(values);
    },
  });

  // Update validation schema when country changes
  useEffect(() => {
    formik.setFieldValue('phone', ''); // Clear phone when switching
  }, [phoneCountry]);



  const handleRegistration = async (values) => {
    try {
      setRegistrationError('');
      setIsRegistering(true);
      const { email, fullName, phone, password, referralCode, role, companyName } = values;

      const userData = { email, fullName, phone, password, role, referralCode, companyName };
      const response = await authService.register(userData);

      // Check if verification is required
      if (response.verificationRequired) {
        setVerificationData({ phone: response.phone, role });
        setVerificationStep(true);
        setResendTimer(30); // Start 30 second timer
        return;
      }

      // If no verification needed, redirect to appropriate dashboard
      if (role === 'client') navigate('/client/dashboard');
      else navigate('/respondent/dashboard');
    } catch (error) {
      console.error('Registration error:', error);
      
      // Handle rate limit error
      if (error.response?.status === 429) {
        setRegistrationError(error.response?.data?.error || 'Слишком много попыток. Пожалуйста, подождите.');
      } else if (error.response?.status === 400) {
        setRegistrationError('Этот номер телефона уже зарегистрирован');
      } else {
        setRegistrationError(
          error.message || 
          error.response?.data?.error || 
          'Не удалось зарегистрироваться. Попробуйте еще раз.'
        );
      }
    } finally {
      setIsRegistering(false);
    }
  };

  const handleVerification = async () => {
    try {
      setVerificationError('');
      setIsVerifying(true);
      
      const response = await authService.verifyPhone(verificationData.phone, verificationCode);
      
      // authService.verifyPhone now stores token/user automatically
      // Redirect to appropriate dashboard
      if (verificationData.role === 'client') navigate('/client/dashboard');
      else navigate('/respondent/dashboard');
    } catch (error) {
      console.error('Verification error:', error);
      setVerificationError(
        error.response?.data?.error || 
        error.message || 
        'Неверный код. Попробуйте еще раз.'
      );
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    try {
      setVerificationError('');
      setResendSuccess(false);
      setIsResending(true);
      
      await authService.resendVerificationCode(verificationData.phone);
      setResendSuccess(true);
      setResendTimer(30); // Restart 30 second timer
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => setResendSuccess(false), 3000);
    } catch (error) {
      console.error('Resend error:', error);
      setVerificationError(
        error.response?.data?.error || 
        error.message || 
        'Не удалось отправить код повторно. Попробуйте позже.'
      );
    } finally {
      setIsResending(false);
    }
  };



  return (
    <Container component="main" maxWidth="sm">
      <Paper 
        elevation={0} 
        sx={{ 
          p: { xs: 3, md: 4 },
          mt: 8,
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '24px', md: '32px' },
          border: '1px solid rgba(255, 255, 255, 0.3)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
        }}
      >
        <Typography 
          component="h1" 
          variant="h4" 
          align="center" 
          gutterBottom
          sx={{
            fontWeight: 700,
            color: '#2d3748',
            mb: 3
          }}
        >
          {verificationStep
            ? 'Подтверждение номера телефона'
            : 'Регистрация респондента'}
        </Typography>

        {!verificationStep ? (
          <Box component="form" onSubmit={formik.handleSubmit}>
            {registrationError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.1), rgba(214, 48, 49, 0.1))',
                  border: '1px solid rgba(214, 48, 49, 0.3)',
                  '& .MuiAlert-icon': { color: '#d63031' },
                  '& .MuiAlert-message': { color: '#d63031', fontWeight: 600 }
                }}
              >
                {registrationError}
              </Alert>
            )}

            <>
              <TextField
                fullWidth
                margin="normal"
                name="email"
                label="Электронная почта"
                value={formik.values.email}
                onChange={formik.handleChange}
                error={formik.touched.email && Boolean(formik.errors.email)}
                helperText={formik.touched.email && formik.errors.email}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0984e3',
                    fontWeight: 600
                  }
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                name="fullName"
                label="Полное имя"
                value={formik.values.fullName}
                onChange={formik.handleChange}
                error={formik.touched.fullName && Boolean(formik.errors.fullName)}
                helperText={formik.touched.fullName && formik.errors.fullName}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0984e3',
                    fontWeight: 600
                  }
                }}
              />
              
              <Box sx={{ mt: 2, mb: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    mb: 1.5,
                    fontWeight: 600,
                    color: '#2d3748'
                  }}
                >
                  Страна номера телефона:
                </Typography>
                <ToggleButtonGroup
                  value={phoneCountry}
                  exclusive
                  onChange={(e, newValue) => {
                    if (newValue !== null) {
                      setPhoneCountry(newValue);
                    }
                  }}
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      borderRadius: '12px',
                      fontWeight: 600,
                      textTransform: 'none',
                      py: 1,
                      '&.Mui-selected': {
                        background: 'linear-gradient(135deg, #74b9ff 0%, #0984e3 100%)',
                        color: 'white',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #0984e3 0%, #74b9ff 100%)'
                        }
                      }
                    }
                  }}
                >
                  <ToggleButton value="BY">
                    🇧🇾 Беларусь (+375)
                  </ToggleButton>
                  {/* Russian numbers temporarily disabled */}
                  {/* <ToggleButton value="RU">
                    🇷🇺 Россия (+7)
                  </ToggleButton> */}
                </ToggleButtonGroup>
              </Box>

              <TextField
                fullWidth
                margin="normal"
                name="phone"
                label="Номер телефона"
                required
                value={formik.values.phone}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0984e3',
                    fontWeight: 600
                  }
                }}
                onChange={(e) => {
                  let value = e.target.value;
                  // Remove all non-digits
                  value = value.replace(/\D/g, '');
                  
                  // Only Belarusian numbers for now
                  // Ensure it starts with 375
                  if (value.length > 0 && !value.startsWith('375')) {
                    value = '375' + value;
                  }
                  // Limit to 12 digits (375 + 9 digits)
                  if (value.length > 12) {
                    value = value.substring(0, 12);
                  }
                  
                  // Russian numbers temporarily disabled
                  // if (phoneCountry === 'BY') {
                  //   if (value.length > 0 && !value.startsWith('375')) {
                  //     value = '375' + value;
                  //   }
                  //   if (value.length > 12) {
                  //     value = value.substring(0, 12);
                  //   }
                  // } else {
                  //   if (value.length > 0 && !value.startsWith('7')) {
                  //     value = '7' + value;
                  //   }
                  //   if (value.length > 11) {
                  //     value = value.substring(0, 11);
                  //   }
                  // }
                  
                  formik.setFieldValue('phone', value ? '+' + value : '');
                }}
                error={formik.touched.phone && Boolean(formik.errors.phone)}
                helperText={formik.touched.phone && formik.errors.phone}
                placeholder='+375 29 123 45 67'
                InputProps={{
                  startAdornment: (
                    <Box component="span" sx={{ mr: 1, fontSize: '1.5rem' }}>
                      🇧🇾
                    </Box>
                  ),
                }}
              />
              <TextField
                fullWidth
                margin="normal"
                name="password"
                label="Пароль"
                type={showPassword ? 'text' : 'password'}
                value={formik.values.password}
                onChange={formik.handleChange}
                error={formik.touched.password && Boolean(formik.errors.password)}
                helperText={formik.touched.password && formik.errors.password}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword(!showPassword)}
                        edge="end"
                      >
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '12px',
                    '&.Mui-focused fieldset': {
                      borderColor: '#0984e3',
                      borderWidth: 2
                    }
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: '#0984e3',
                    fontWeight: 600
                  }
                }}
              />
              {effectiveRole === 'client' && (
                <TextField
                  fullWidth
                  margin="normal"
                  name="companyName"
                  label="Название компании"
                  required
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  error={formik.touched.companyName && Boolean(formik.errors.companyName)}
                  helperText={formik.touched.companyName && formik.errors.companyName}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused fieldset': {
                        borderColor: '#0984e3',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#0984e3',
                      fontWeight: 600
                    }
                  }}
                />
              )}
              {/* Role is determined by URL and not shown in the form */}
              {effectiveRole === 'respondent' && (
                <TextField
                  fullWidth
                  margin="normal"
                  name="referralCode"
                  label="Бонусный код (+0.3 балла за регистрацию)"
                  value={formik.values.referralCode}
                  onChange={formik.handleChange}
                  error={formik.touched.referralCode && Boolean(formik.errors.referralCode)}
                  helperText={formik.touched.referralCode && formik.errors.referralCode}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '12px',
                      '&.Mui-focused fieldset': {
                        borderColor: '#0984e3',
                        borderWidth: 2
                      }
                    },
                    '& .MuiInputLabel-root.Mui-focused': {
                      color: '#0984e3',
                      fontWeight: 600
                    }
                  }}
                />
              )}
            </>

            {effectiveRole === 'respondent' && (
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    sx={{
                      color: '#56ab2f',
                      '&.Mui-checked': {
                        color: '#56ab2f'
                      }
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Регистрируясь, вы принимаете{' '}
                    <Link
                      href={`${process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000'}/uploads/thinklink_agreement.pdf`}
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: '#0984e3',
                        textDecoration: 'none',
                        fontWeight: 600,
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Пользовательское соглашение - публичную оферту
                    </Link>
                  </Typography>
                }
                sx={{ mt: 2, mb: 1 }}
              />
            )}

            <Button
              type="submit"
              fullWidth
              variant="contained"
              sx={{ 
                mt: effectiveRole === 'respondent' ? 2 : 3,
                mb: 2,
                background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                py: 1.5,
                fontSize: '1rem',
                boxShadow: '0 4px 20px rgba(86, 171, 47, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)',
                  boxShadow: '0 6px 25px rgba(86, 171, 47, 0.5)'
                },
                '&.Mui-disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
              disabled={isRegistering || (effectiveRole === 'respondent' && !acceptedTerms)}
              startIcon={isRegistering ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isRegistering ? 'Регистрация...' : 'Зарегистрироваться'}
            </Button>
          </Box>
        ) : (
          <Box>
            {verificationError && (
              <Alert 
                severity="error" 
                sx={{ 
                  mb: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(255, 118, 117, 0.1), rgba(214, 48, 49, 0.1))',
                  border: '1px solid rgba(214, 48, 49, 0.3)',
                  '& .MuiAlert-icon': { color: '#d63031' },
                  '& .MuiAlert-message': { color: '#d63031', fontWeight: 600 }
                }}
              >
                {verificationError}
              </Alert>
            )}
            
            {resendSuccess && (
              <Alert 
                severity="success" 
                sx={{ 
                  mb: 2,
                  borderRadius: '12px',
                  background: 'linear-gradient(135deg, rgba(86, 171, 47, 0.1), rgba(45, 122, 31, 0.1))',
                  border: '1px solid rgba(86, 171, 47, 0.3)',
                  '& .MuiAlert-icon': { color: '#56ab2f' },
                  '& .MuiAlert-message': { color: '#56ab2f', fontWeight: 600 }
                }}
              >
                Код отправлен повторно
              </Alert>
            )}
            
            <Alert 
              severity="info" 
              sx={{ 
                mb: 3,
                borderRadius: '12px',
                background: 'linear-gradient(135deg, rgba(116, 185, 255, 0.1), rgba(9, 132, 227, 0.1))',
                border: '1px solid rgba(9, 132, 227, 0.3)',
                '& .MuiAlert-icon': { color: '#0984e3' },
                '& .MuiAlert-message': { color: '#2d3748', fontWeight: 600 }
              }}
            >
              На номер {verificationData.phone} отправлено SMS с кодом подтверждения
            </Alert>

            <TextField
              fullWidth
              margin="normal"
              label="Код из SMS (6 цифр)"
              value={verificationCode}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '').substring(0, 6);
                setVerificationCode(value);
              }}
              placeholder="123456"
              inputProps={{ 
                maxLength: 6,
                style: { fontSize: '1.5rem', textAlign: 'center', letterSpacing: '0.5rem' }
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: '16px',
                  background: 'rgba(116, 185, 255, 0.05)',
                  '&.Mui-focused fieldset': {
                    borderColor: '#0984e3',
                    borderWidth: 2
                  }
                },
                '& .MuiInputLabel-root.Mui-focused': {
                  color: '#0984e3',
                  fontWeight: 600
                }
              }}
            />

            <Button
              fullWidth
              variant="contained"
              sx={{ 
                mt: 3,
                mb: 2,
                background: 'linear-gradient(135deg, #56ab2f 0%, #2d7a1f 100%)',
                color: 'white',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                py: 1.5,
                fontSize: '1rem',
                boxShadow: '0 4px 20px rgba(86, 171, 47, 0.4)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #2d7a1f 0%, #56ab2f 100%)',
                  boxShadow: '0 6px 25px rgba(86, 171, 47, 0.5)'
                },
                '&.Mui-disabled': {
                  background: 'rgba(0, 0, 0, 0.12)',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
              onClick={handleVerification}
              disabled={verificationCode.length !== 6 || isVerifying}
              startIcon={isVerifying ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isVerifying ? 'Проверка...' : 'Подтвердить'}
            </Button>

            <Button
              fullWidth
              variant="outlined"
              sx={{ 
                mb: 1,
                color: '#0984e3',
                borderColor: '#0984e3',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                py: 1.2,
                borderWidth: 2,
                '&:hover': {
                  borderWidth: 2,
                  borderColor: '#0984e3',
                  background: 'rgba(116, 185, 255, 0.1)'
                },
                '&.Mui-disabled': {
                  color: 'rgba(0, 0, 0, 0.26)',
                  borderColor: 'rgba(0, 0, 0, 0.12)'
                }
              }}
              onClick={handleResendCode}
              disabled={isResending || resendTimer > 0}
              startIcon={isResending ? <CircularProgress size={20} color="inherit" /> : null}
            >
              {isResending ? 'Отправка...' : resendTimer > 0 ? `Отправить код повторно (${resendTimer}с)` : 'Отправить код повторно'}
            </Button>

            <Button
              fullWidth
              variant="text"
              onClick={() => {
                setVerificationStep(false);
                setVerificationCode('');
                setVerificationError('');
              }}
              sx={{
                color: '#718096',
                fontWeight: 600,
                textTransform: 'none',
                borderRadius: '12px',
                py: 1,
                '&:hover': {
                  background: 'rgba(113, 128, 150, 0.1)'
                }
              }}
            >
              Изменить данные
            </Button>
          </Box>
        )}
      </Paper>
    </Container>
  );
}